import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import domtoimage from "dom-to-image";

import styles from "../../../styles/app/AppStories.module.css";
import "swiper/css";

import { storyColorMap } from "@/components/results/stories/storyColorMap";
import CloseButtonSummary from "@/components/shared/CloseButtonSummary";
import {
  replaceDescription,
  replaceTitle,
} from "@/components/results/stories/storiesTextFunctions";
import { useModalContext } from "@/context/ModalContext";
import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import { HappySadFaceIcon } from "../Snippets/SnippetInterractionBar/SnippetInterractionBar";
import {
  PeriodicQuizInstagramIcon,
  PeriodicQuizSnapchatIcon,
} from "@/components/appHome/PeriodicQuizzes/PeriodicQuizShareIcons";
import { DailyStoryFeedbackScoreBar } from "../personalityAnalysis/AnalysisFeedback/AnalysisFeedback";
import { Mixpanel } from "@/helpers/mixpanel";
import { DiscoveredStory } from "@dimensional-engineering/dimensional-models";
import { useAuth } from "@/context/AuthContext";

type Props = {
  storySlug: string;
};

export default function SwipableStoriesModal(props: Props) {
  const [mySwiperRef, setMySwiperRef] = useState<any>(null);
  const [index, setIndex] = useState<number>(0);

  const [showRateModule, setShowRateModule] = useState<boolean>(false);

  const discoveredStories = useSelector(
    (state: RootState) => state.user.discoveredStories
  );
  const { setModalOpen, setModalComponent } = useModalContext();
  const { user } = useAuth();

  const mappedStories = discoveredStories?.map((story) => {
    const title = story?.name.includes("|")
      ? replaceTitle(story?.name, false)
      : story?.name;

    const mappedDescription = story?.storyDetail?.listicle?.descriptors.map(
      (desc, index) => {
        return (
          <div className={styles.descDiv} key={index}>
            <div
              onClick={() => {
                setModalOpen(false);
              }}
              className={styles.xmark}
            >
              <CloseButtonSummary />
            </div>
            <div
              style={
                story.storySlug
                  ? {
                      backgroundColor:
                        storyColorMap[
                          story.storySlug as keyof typeof storyColorMap
                        ],
                    }
                  : {}
              }
              className={styles.circle}
            >
              <p
                style={{
                  backgroundImage: `url(/stories/${story.storySlug}.png)`,
                }}
              >
                {index + 1}
              </p>
            </div>
            <h4
              style={
                story.storySlug
                  ? {
                      color:
                        storyColorMap[
                          story.storySlug as keyof typeof storyColorMap
                        ],
                    }
                  : {}
              }
            >
              {desc.includes("|") ? replaceDescription(desc) : desc}
            </h4>
          </div>
        );
      }
    );

    return (
      <SwiperSlide key={story.storySlug}>
        <div
          style={{
            backgroundImage: `url(/stories/${story.storySlug}.png)`,
          }}
          className={styles.slugMain}
        >
          <h1
            style={
              story.storySlug
                ? {
                    color:
                      storyColorMap[
                        story.storySlug as keyof typeof storyColorMap
                      ],
                  }
                : {}
            }
          >
            {title}
          </h1>
          <div className={styles.slugMappedDiv}>{mappedDescription}</div>
          {showRateModule && (
            <div className={styles.feedbackDiv}>
              <DailyStoryFeedbackScoreBar
                onAnswer={async (value) => {
                  Mixpanel?.track(
                    "Feedback given",
                    {
                      score: value,
                      content_type: "listicle story",
                      slug: story.storySlug,
                    },
                    user?.uid
                  );
                  setTimeout(() => {
                    setShowRateModule(false);
                  }, 500);
                }}
                title="How good was this content?"
                subtitle="Feedback improves your results"
                currentAnswer={null}
                onClose={() => setShowRateModule(false)}
              />
            </div>
          )}
          <div className={styles.interactionDiv}>
            <div
              onClick={() => {
                setShowRateModule(true);
              }}
              className={styles.buttonDiv}
            >
              <HappySadFaceIcon />
              <p className={styles.text}>Rate</p>
            </div>
            <div className={styles.shareDivParent}>
              <div className={styles.shareDiv}>
                <PeriodicQuizInstagramIcon
                  onClick={() => {
                    setModalComponent(
                      <StoriesShareView type="instagram" story={story} />
                    );
                    Mixpanel?.track(
                      "Shared to social",
                      {
                        platform: "Instagram",
                        slug: story.storySlug,
                        type: "listicle story",
                      },
                      user?.uid
                    );
                  }}
                />
                <PeriodicQuizSnapchatIcon
                  onClick={() => {
                    setModalComponent(
                      <StoriesShareView type="snapchat" story={story} />
                    );
                    Mixpanel?.track(
                      "Shared to social",
                      {
                        platform: "Snapchat",
                        slug: story.storySlug,
                        type: "listicle story",
                      },
                      user?.uid
                    );
                  }}
                />
              </div>
              <p className={styles.shareToText}>Share to</p>
            </div>
          </div>
        </div>
      </SwiperSlide>
    );
  });

  useEffect(() => {
    if (discoveredStories?.length) {
      for (let i = 0; i < discoveredStories?.length; i++) {
        if (discoveredStories[i].storySlug === props.storySlug) {
          setIndex(i);
        }
      }
    }
  }, [props.storySlug]);

  useEffect(() => {
    if (mySwiperRef) {
      mySwiperRef?.slideTo(index);
      setShowRateModule(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  return (
    <ModalWrapper title="" noHeader>
      <div style={{ width: "100%" }}>
        <Swiper
          min-height={200}
          autoHeight={false}
          slidesPerView={1}
          touchReleaseOnEdges={true}
          scrollbar={{ draggable: true }}
          threshold={5}
          onSwiper={setMySwiperRef}
        >
          {mappedStories}
        </Swiper>
      </div>
    </ModalWrapper>
  );
}

function StoriesShareView(props: {
  story: DiscoveredStory;
  type: "instagram" | "snapchat";
}) {
  const imageRef = useRef<HTMLDivElement>(null);
  const { setModalOpen } = useModalContext();

  useEffect(() => {
    const handleDownloadImage = async () => {
      if (imageRef.current) {
        const dataUrl = await domtoimage.toJpeg(imageRef.current, {
          quality: 0.95,
        });
        // const link = document.createElement("a");
        // link.href = dataUrl;
        // link.download = "somefilename.jpg";
        // link.click();
        if (window.Android) {
          window.Android.onHaptic();

          if (props.type === "instagram") {
            window.Android.shareToInstagramQuiz(dataUrl);
          } else {
            window.Android.shareToSnapChat(dataUrl);
          }
        }
      }
    };

    setTimeout(() => {
      handleDownloadImage().then(() => {
        setModalOpen(false);
      });
    }, 50);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = props.story?.name.includes("|")
    ? replaceTitle(props.story?.name, false)
    : props.story?.name;

  const mappedDescription = props.story?.storyDetail?.listicle?.descriptors.map(
    (desc, index) => {
      return (
        <div className={styles.descDiv} key={index}>
          <div
            style={
              props.story.storySlug
                ? {
                    backgroundColor:
                      storyColorMap[
                        props.story.storySlug as keyof typeof storyColorMap
                      ],
                  }
                : {}
            }
            className={styles.circle}
          >
            <p
              style={{
                backgroundImage: `url(/stories/${props.story.storySlug}.png)`,
              }}
            >
              {index + 1}
            </p>
          </div>
          <h4
            style={
              props.story.storySlug
                ? {
                    color:
                      storyColorMap[
                        props.story.storySlug as keyof typeof storyColorMap
                      ],
                  }
                : {}
            }
          >
            {desc.includes("|") ? replaceDescription(desc) : desc}
          </h4>
        </div>
      );
    }
  );

  return (
    <ModalWrapper noHeader noLine title="">
      <main
        style={{
          backgroundImage: `url(/stories/${props.story.storySlug}.png)`,
          justifyContent: "center",
          padding: "0 15px",
        }}
        className={styles.slugMain}
        ref={imageRef}
      >
        <h1
          style={
            props.story.storySlug
              ? {
                  color:
                    storyColorMap[
                      props.story.storySlug as keyof typeof storyColorMap
                    ],
                }
              : {}
          }
        >
          {title}
        </h1>
        <div className={styles.slugMappedDiv}>{mappedDescription}</div>
      </main>
    </ModalWrapper>
  );
}
